import { getStoredMeta, loadStoredSession } from "@itaas/core/auth/auth.storage";
import type { RevenueBridgeInvoice } from "@/core/billing/revenue-bridge";
import type { InvoiceBillingSync, ValidatedInvoice } from "@/core/pharmacy/types";
import { getPharmacySettings } from "@/core/admin/establishment-store";
import { updateValidatedInvoice } from "@/core/pharmacy/pharmacy-store";

const BILLING_API_BASE =
  import.meta.env.VITE_BILLING_API_BASE_URL ?? "/api/billing";

const SYNC_STATE_KEY = "hospitol.billing.lastSync";

export interface BillingSyncState {
  at: string;
  synced: number;
  skipped: number;
  failed: number;
}

function tenantSlug(): string {
  return loadStoredSession()?.tenant?.slug ?? "demo-hospitol";
}

async function billingFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Tenant-Slug": tenantSlug(),
  };
  const { accessToken } = getStoredMeta();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

  const response = await fetch(`${BILLING_API_BASE}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers as Record<string, string>) },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `Billing API ${response.status}`);
  }
  return (await response.json()) as T;
}

export function invoiceToBridgeItem(inv: ValidatedInvoice, currency: string): RevenueBridgeInvoice {
  return {
    externalId: inv.id,
    number: inv.number,
    validatedAt: inv.validatedAt,
    subtotal: inv.subtotal,
    currency,
    customerName: inv.customer.name,
    lineCount: inv.lines.length,
    ...(inv.patientId ? { patientId: inv.patientId } : {}),
    ...(inv.prescriptionId ? { prescriptionId: inv.prescriptionId } : {}),
    source: "hospitol.pharmacy",
  };
}

function resultToSyncStatus(result: {
  synced: number;
  skipped: number;
  failed: number;
}): InvoiceBillingSync {
  const at = new Date().toISOString();
  if (result.synced > 0) return { status: "synced", at };
  if (result.skipped > 0) return { status: "skipped", at };
  return { status: "failed", at, error: "Synchronisation refusée" };
}

export async function syncInvoiceToBilling(
  invoice: ValidatedInvoice,
): Promise<InvoiceBillingSync> {
  if (!getPharmacySettings().syncBillingOnInvoice) {
    return { status: "pending" };
  }

  const { currency } = getPharmacySettings();
  try {
    const result = await billingFetch<{ synced: number; skipped: number; failed: number }>(
      "/integrations/sync/pharmacy-invoices",
      {
        method: "POST",
        body: JSON.stringify({ invoices: [invoiceToBridgeItem(invoice, currency)] }),
      },
    );
    const billingSync = resultToSyncStatus(result);
    updateValidatedInvoice(invoice.id, { billingSync });
    return billingSync;
  } catch (err) {
    const billingSync: InvoiceBillingSync = {
      status: "failed",
      at: new Date().toISOString(),
      error: err instanceof Error ? err.message : "Erreur sync billing",
    };
    updateValidatedInvoice(invoice.id, { billingSync });
    return billingSync;
  }
}

export async function syncAllInvoicesToBilling(
  invoices: ValidatedInvoice[],
): Promise<BillingSyncState> {
  let synced = 0;
  let skipped = 0;
  let failed = 0;

  for (const invoice of invoices) {
    const result = await syncInvoiceToBilling(invoice);
    if (result.status === "synced") synced += 1;
    else if (result.status === "skipped") skipped += 1;
    else if (result.status === "failed") failed += 1;
  }

  const state: BillingSyncState = {
    at: new Date().toISOString(),
    synced,
    skipped,
    failed,
  };
  localStorage.setItem(SYNC_STATE_KEY, JSON.stringify(state));
  return state;
}

export function loadBillingSyncState(): BillingSyncState | null {
  try {
    const raw = localStorage.getItem(SYNC_STATE_KEY);
    return raw ? (JSON.parse(raw) as BillingSyncState) : null;
  } catch {
    return null;
  }
}
